import { FC, ReactNode, MouseEvent } from 'react';

import { Box, css, DialogActions, DialogContent, DialogTitle, styled } from '@mui/material';
import DialogContentText from '@mui/material/DialogContentText';

import { Dialogs as DialogAnalyticsId, trackDialogAnalyticsEvent } from 'analytics/events/dialog';
import { messageDialogSelectors } from 'tests/models/components/dialog/message-dialog.selectors';

import { Testable } from 'utils/TypeUtils';

import IconButton from 'components/Buttons/IconButton';
import Icon from 'components/Icons/Icon';
import { FilledButton, OutlinedButton } from 'components/UIkit/atoms/Button';

import { Dialog } from './Dialog';
import { DialogCloseReason } from './MessageDialog.constants';
import { DialogActionButtonProps } from './MessageDialog.types';
import { closeReasonToAnaltiycsEventAction } from './MessageDialog.utils';

export interface MessageDialogProps extends Testable {
  id: DialogAnalyticsId;
  /** Whether the dialog is shown. */
  isOpen: boolean;
  /** Should set the `isOpen` prop to false. */
  handleClose: (reason: DialogCloseReason) => void;
  title: string;
  children?: ReactNode;
  /** Whether to prevent the dialog from closing when clicking the corner `x` button. */
  disableClosing?: boolean;
  primaryActionProps: DialogActionButtonProps;
  secondaryActionProps?: DialogActionButtonProps;
  tertiaryActionProps?: DialogActionButtonProps;
  disableAutoFocus?: boolean;
}

/** A message dialog that displays a message to the user and allows customizable action buttons. Events are tracked to analytics. */
export const MessageDialog: FC<MessageDialogProps> = ({
  id,
  isOpen,
  handleClose,
  title,
  children,
  primaryActionProps,
  secondaryActionProps,
  tertiaryActionProps,
  disableClosing = false,
  disableAutoFocus,
  testHook
}) => {
  const handleActionClick = (
    buttonProps: DialogActionButtonProps,
    event: MouseEvent<HTMLButtonElement>
  ) => {
    trackDialogAnalyticsEvent(buttonProps.text, id);
    buttonProps.onClick(event);
  };

  let dialogActions = (
    <>
      {secondaryActionProps && (
        <OutlinedButton
          display="contents"
          fullWidth
          onClick={(event) => handleActionClick(secondaryActionProps, event)}
          disabled={secondaryActionProps.disabled}
          testHook={messageDialogSelectors.cancelButton(testHook)}
        >
          {secondaryActionProps.text}
        </OutlinedButton>
      )}
      {primaryActionProps && (
        <FilledButton
          display="contents"
          fullWidth
          testHook={messageDialogSelectors.submitButton(testHook)}
          onClick={(event) => handleActionClick(primaryActionProps, event)}
          disabled={primaryActionProps.disabled}
        >
          {primaryActionProps.text}
        </FilledButton>
      )}
    </>
  );
  if (tertiaryActionProps && secondaryActionProps) {
    dialogActions = (
      <StyledStackedButtonsContainer>
        <FilledButton
          display="contents"
          fullWidth
          testHook={messageDialogSelectors.submitButton(testHook)}
          onClick={(event) => handleActionClick(primaryActionProps, event)}
          disabled={primaryActionProps.disabled}
        >
          {primaryActionProps.text}
        </FilledButton>
        <OutlinedButton
          display="contents"
          fullWidth
          onClick={(event) => handleActionClick(secondaryActionProps, event)}
          disabled={secondaryActionProps.disabled}
          testHook={messageDialogSelectors.cancelButton(testHook)}
        >
          {secondaryActionProps.text}
        </OutlinedButton>
        <StyledClosedAction>
          <OutlinedButton
            display="contents"
            fullWidth
            onClick={(event) => handleActionClick(tertiaryActionProps, event)}
            disabled={tertiaryActionProps.disabled}
          >
            {tertiaryActionProps.text}
          </OutlinedButton>
        </StyledClosedAction>
      </StyledStackedButtonsContainer>
    );
  }
  return (
    <Dialog
      isOpen={isOpen}
      handleClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      testHook={isOpen ? testHook : ''}
      disableAutoFocus={disableAutoFocus}
    >
      <DialogTitle>
        <StyledCloseButton
          disabled={disableClosing}
          aria-label="close"
          onClick={() => {
            if (!disableClosing) {
              trackDialogAnalyticsEvent(
                closeReasonToAnaltiycsEventAction(DialogCloseReason.CloseButtonClick),
                id
              );
              handleClose && handleClose(DialogCloseReason.CloseButtonClick);
            }
          }}
        >
          <Icon.XThick />
        </StyledCloseButton>
        {title}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>{children}</DialogContentText>
      </DialogContent>
      <DialogActions>{dialogActions}</DialogActions>
    </Dialog>
  );
};

const StyledCloseButton = styled(IconButton)(
  ({ theme }) => css`
    position: absolute;
    top: ${theme.spacing(8)};
    right: ${theme.spacing(8)};
    color: ${theme.palette.natural.black};
  `
);
const StyledClosedAction = styled(Box)(
  ({ theme }) => css`
    margin-top: ${theme.spacing(8)};
  `
);
const StyledStackedButtonsContainer = styled(Box)(
  ({ theme }) => css`
    display: flex;
    width: 100%;
    flex-direction: column;
    gap: ${theme.spacing(12)};
  `
);
